import React from 'react'
import PropTypes from 'prop-types';

import {ValidationResult, validate, required, limitedLength, format, atLeastOneNotEmpty, limitedFileSize, limitedFileType, focusFirstError} from 'validation'
import BaseComponent from 'common/BaseComponent'
import ErrorBox from 'common/ErrorBox'
import ProgressBar from 'common/ProgressBar'
import FileUploader from 'common/FileUploader'
import Input from 'common/Input'
import TextArea from 'common/TextArea'
import theme from 'dialogs/Complaint.scss'
import conf from 'configuration.json'
import {getMessage as msg} from 'MessageBundle'

const MAX_FILE_SIZE = 5*1024*1024;
const ANONYMOUS_ALLOWED_TYPES = ["application/pdf", "image/jpg", "image/jpeg", "image/png", "image/tiff"];
const ALLOWED_TYPES = ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].concat(ANONYMOUS_ALLOWED_TYPES);

export default class Form extends BaseComponent {

    constructor(props) {
        super(props);

        this.allowedTypes = props.authenticated ? ALLOWED_TYPES : ANONYMOUS_ALLOWED_TYPES;
        this.validation = this.getValidation(this.allowedTypes);
        this.elementOrder = {
            vo : "vo",
            complaintNumber : "complaintNumber",
            comment : "comment",
            attachments : "fileUploader"
        };
        this.state = this.getInitialState();
        this.state.complaintNumber = props.complaintNumber;
        this.state.processing = false;
        if (props.vo !== undefined) {
            // VPQ Operators have dummy VO that should not be filled
            this.state.vo = !props.vpqOperator ? props.vo : "";
        }
    }

    getInitialState = () => {
        return {
            vo: this.props.authenticated && !this.props.vpqOperator ? this.props.vo : "",
            complaintNumber: "",
            comment: "",
            attachments: [],
            errorMessages: {}
        }
    };

    getValidation(allowedFileTypes) {
        return {
            main: [
                {
                    properties: ["state.vo"],
                    individualNames: ["vo"],
                    strategies: [
                        required(msg("extendComplaintValidation.voFieldEmpty")),
                        limitedLength(5, 5, msg("extendComplaintValidation.voFormat"))
                    ]
                }, {
                    properties: ["state.complaintNumber"],
                    individualNames: ["complaintNumber"],
                    strategies: [
                        required(msg("extendComplaintValidation.complaintNumberEmpty")),
                        format(/[R]\d{7}|^[0-9]{6}$/, msg("extendComplaintValidation.complaintNumberFormat"))
                    ]
                }, {
                    properties: ["state.comment", "state.attachments"],
                    individualNames: ["comment", "attachments"],
                    strategies: [
                        atLeastOneNotEmpty(msg("extendComplaintValidation.voAndComplaintEmpty"))
                    ]
                }, {
                    properties: ["state.attachments"],
                    individualNames: ["attachments"],
                    strategies: [
                        limitedFileSize(MAX_FILE_SIZE, msg("extendComplaintValidation.fileSizeError")),
                        limitedFileType(allowedFileTypes, msg("extendComplaintValidation.fileTypeError"))
                    ]
                }
            ]
        };
    }

    getProcessingErrors = () => {
        return new Map([
            ["WRONG_COMPLAINT_NUMBER", {
                message: msg("extendComplaintValidation.notFound"),
                invalidComponent: "complaintNumber"
            }],
            ["WRONG_VO", {
                message: msg("extendComplaintValidation.voMismatch"),
                invalidComponent: "vo"
            }],
            ["READ_ONLY_COMPLAINT", {
                message: msg("extendComplaintValidation.readOnly"),
                invalidComponent: "complaintNumber"
            }],
            ["INVALID_ATTACHMENT_CONTENT", {
                message: msg("common.attachmentValidationFault"),
                invalidComponent: "attachments"
            }],
            ["INVALID_ATTACHMENT_SIZE", {
                message: msg("extendComplaintValidation.fileSizeError"),
                invalidComponent: "attachments"
            }]
        ]);
    };

    handleReset = () => {
        this.setState(this.getInitialState());
    };

    handleSubmit = () => {
        const validationResult = validate(this, this.validation, ["main"]);
        if (validationResult.isEmpty()) {
            this.setState({
                processing: true,
                errorMessages: {}
            });
            let formData = new FormData();
            formData.append("vo", new Blob([this.state.vo], {type: "application/json"}));
            formData.append("complaintNumber", new Blob([this.state.complaintNumber], {type: "application/json"}));
            formData.append("comment", new Blob([this.state.comment], {type: "application/json"}));

            this.uploadAttachments(this.state.attachments, formData, "attachments", this.updateComplaint);
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    updateComplaint = (formData) => {
        this.request('put', conf.rest.complaintEndpoint).params(formData).send((result) => {
            this.props.onComplete(result.body);
        }, (error, result) => {
            let errorText;
            let invalidComponent;
            if (result && result.statusCode === 412) {
                const errorDescriptor = this.getProcessingErrors().get(result.body.message);
                errorText = errorDescriptor.message;
                invalidComponent = errorDescriptor.invalidComponent;
            } else if (result && result.status === 403) {
                errorText = msg("extendComplaintValidation.intrusionLocked");
                invalidComponent = [];
            } else {
                errorText = msg("common.technicalError" );
                invalidComponent = [];
            }
            const validationResult = new ValidationResult(this.state.errorMessages);
            validationResult.setGroup("manual", {[invalidComponent]: [errorText]});
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages,
                processing: false
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        });
    };

    render() {
        return (
            <div>
                <h2>{msg('extendComplaint.header')}</h2>
                <ErrorBox groups={["main", "manual"]} errorMessages={this.state.errorMessages}/>
                <div className={theme.row + " " + theme.rowAddInfo}>
                   <Input id="vo" value={this.state.vo} name="vo" errorMessages={this.state.errorMessages} onChange={this.handleChange("vo")}
                       required label={msg("extendComplaint.vo")} disabled={this.props.authenticated && !this.props.vpqOperator}/>
                </div>
                <div className={theme.row + " " + theme.rowAddInfo}>
                    <Input id="complaintNumber" value={this.state.complaintNumber} name="complaintNumber" errorMessages={this.state.errorMessages}
                        onChange={this.handleChange("complaintNumber")} required label={msg("extendComplaint.complaintNumber")}/>
                </div>
                <div className={theme.clear + " " + theme.rowDecor + " " + theme.rowAddInfo}>
                    <div className={theme.coll}>
                        <div className={theme.rowComment}>
                            <TextArea id="comment" value={this.state.comment} max={4000} name="comment" errorMessages={this.state.errorMessages}
                                onChange={this.handleChange("comment")} label={msg("extendComplaint.comment")} tooltip={msg("extendComplaint.commentTooltip")}/>
                        </div>
                    </div>
                    <div className={theme.colr}>
                        <FileUploader id="fileUploader" value={this.state.attachments} allowedTypes={this.allowedTypes}
                            className={theme.rowFileUpload} authenticated={this.props.authenticated} name="attachments"
                            errorMessages={this.state.errorMessages} onChange={this.handleChange("attachments")}/>
                    </div>
                </div>
                <div className={theme.btnGroup + " " + theme.btnGroupDecor}>
                    <span><span>*</span>{msg('extendComplaint.mandatoryInfo')}</span>
                    <button id="reset-btn" onClick={this.handleReset}>{msg('extendComplaint.resetBtn')}</button>
                    <button id="cancel-btn" onClick={this.closeWindow}>{msg('extendComplaint.cancelBtn')}</button>
                    <button id="submit-btn" className={theme.accent} onClick={this.handleSubmit}>{msg('extendComplaint.submitBtn')}</button>
                </div>
                <ProgressBar hidden={!this.state.processing} />
            </div>
        )
    }
}

Form.propTypes = {
    vo: PropTypes.string,
    complaintNumber: PropTypes.string,
    authenticated: PropTypes.bool.isRequired,
    vpqOperator: PropTypes.bool.isRequired,
    onSubmit: PropTypes.func,
    onComplete: PropTypes.func
};


// WEBPACK FOOTER //
// ./src/dialogs/ExtendComplaint/Form.js